import React, { useEffect } from "react";
import styles from "./feedback.module.scss";
import {
  PageError,
  PageLoading,
} from "apps/user/learner/components/page-container";
import SessionFeedbackCard from "../../components/cards/SessionFeedbackCard";
import SessionFeedbackSelectButtonCard from "../../components/cards/SessionFeedbackSelectButtonCard";
import { ReactComponent as CloseIcon } from "Assets/icon/icon-delete.svg";
import { ReactComponent as ArrowIcon } from "Assets/icon/icon_forward_white.svg";
import { Button, IconButton } from "components/ui";
import { useSessionFeedback } from "providers/SessionFeedback";
import {
  SessionFeedbackModal,
  SessionFeedbackSubmissionErrorModal,
} from "../../components/modals/session-feedback-modal";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "utility/mixpanel";
import { usePatternBackground } from "providers/pattern-background";
import { useTranslation } from "lib/i18n/use-translation";

const FeedbackByStudentAfterJoinSession = () => {
  const { t } = useTranslation(["feedback", "common"]);
  const [openModal, setOpenModal] = React.useState(false);
  const navigate = useNavigate();
  const {
    postSessionFeedbackLoading,
    postSessionFeedbackError,
    sessionDetailLoading,
    sessionDetailError,
    imageUrl,
    practitionerName,
    title,
    sessionCount,
    step,
    type,
    stepsFraction,
    isPostSessionFeedbackSubmissionError,
    submitLoading,
    groupSessionId,
    setIsPostSessionFeedbackSubmissionError,
    onSelectButtonOption,
    onChangeTextInput,
    onBack,
    onSubmit,
  } = useSessionFeedback();

  const { onChangeBackgroundColor, removePatternOverlay } =
    usePatternBackground();
  const background = "#EEE ";
  useEffect(() => {
    onChangeBackgroundColor(background);
    removePatternOverlay(true);
  }, [onChangeBackgroundColor, background, removePatternOverlay]);

  if (postSessionFeedbackLoading || sessionDetailLoading) {
    return (
      <div className={styles["custom-loader-style"]}>
        <PageLoading />
      </div>
    );
  }

  if (postSessionFeedbackError || sessionDetailError) {
    return (
      <div className={styles["custom-loader-style"]}>
        <PageError />
      </div>
    );
  }

  const options = "options" in step.input ? step.input.options : null;

  const handleContinueToNext = () => {
    if (step.__typename !== "EndStep") {
      if (type === "LIVE") {
        trackEvent("Live Session Feedback Question Answered", {
          "Session ID": groupSessionId,
          "Session Title": title,
          "Step ID": step.id,
          "Step Title": step.titleHtml,
          "Response ID": step.response.id,
          "Response Value": step.response?.value,
        });
      } else {
        trackEvent("Recording Feedback Question Answered", {
          "Recording ID": groupSessionId,
          "Recording Title": title,
          "Step ID": step.id,
          "Step Title": step.titleHtml,
          "Response ID": step.response.id,
          "Response Value": step.response?.value,
        });
      }
      onSelectButtonOption(step.id, step.response.value);
    } else {
      onSubmit();
    }
  };

  const handleBack = () => {
    if (type === "LIVE") {
      trackEvent("Live Session Feedback Back Clicked", {
        "Session ID": groupSessionId,
        "Session Title": title,
        "Step ID": step.id,
        "Step Title": step.titleHtml,
      });
    } else {
      trackEvent("Recording Feedback Back Clicked", {
        "Recording ID": groupSessionId,
        "Recording Title": title,
        "Step ID": step.id,
        "Step Title": step.titleHtml,
      });
    }

    onBack();
  };

  const handleClickCloseIcon = () => {
    if (step.__typename === "StartStep" && !step.response.value) {
      handleCloseFeedbackScreen();
    } else {
      setOpenModal(true);
    }
  };

  const handleCloseFeedbackScreen = () => {
    if (type === "LIVE") {
      trackEvent("Live Session Feedback Exit Clicked", {
        "Session ID": groupSessionId,
        "Session Title": title,
      });
    } else {
      trackEvent("Recording Feedback Exit Clicked", {
        "Recording ID": groupSessionId,
        "Recording Title": title,
      });
    }

    setOpenModal(false);
    removePatternOverlay(false);
    navigate("/user/home");
  };
  const handleStayOnFeedbackScreen = () => {
    setOpenModal(false);
  };

  const handleCloseFeedbackScreenAfterSubmissionError = () => {
    if (type === "LIVE") {
      trackEvent("Live Session Feedback Exit Clicked", {
        "Session ID": groupSessionId,
        "Session Title": title,
      });
    } else {
      trackEvent("Recording Feedback Exit Clicked", {
        "Recording ID": groupSessionId,
        "Recording Title": title,
      });
    }
    setIsPostSessionFeedbackSubmissionError(false);
    removePatternOverlay(false);
    navigate("/user/home");
  };

  const handleRetrySubmission = () => {
    onSubmit();
  };

  return (
    <div className={styles["background-color"]}>
      <IconButton
        className={styles["feedback-by-student-close-icon"]}
        onClick={handleClickCloseIcon}
        icon={<CloseIcon height={12} width={12} />}
        aria-label={t("common:action.close")}
        monochromeColour="black"
        variant="enclosed-secondary"
      />
      <div className={styles["page-container"]}>
        <div className={styles["feedback-by-student-container"]}>
          <SessionFeedbackCard
            title={title}
            imageUrl={imageUrl}
            practitionerName={practitionerName}
            sessionCount={sessionCount}
          />
          <div className={styles["feedback-by-student-select-button-card"]}>
            <div className={styles["feedback-by-student-header"]}>
              <IconButton
                variant="enclosed-secondary"
                shape="square"
                onClick={() => handleBack()}
                disabled={step.__typename === "StartStep" || submitLoading}
                icon={<ArrowIcon className={styles["back-button-icon"]} />}
                aria-label={t("common:action.back")}
                className={styles["back-button"]}
              />

              <div className={styles["feedback-by-student-title"]}>
                <p className={styles["feedback-by-student-title-text"]}>
                  {t("feedback:sessionFeedbackTitle")}
                </p>
                <p className={styles["feedback-by-student-title-number"]}>
                  {stepsFraction}
                </p>
              </div>
            </div>

            <SessionFeedbackSelectButtonCard
              title={step.titleHtml}
              onClick={(id, value) => onSelectButtonOption(id, value)}
              options={options}
              shouldShowButtonLabels={step.__typename === "StartStep"}
              type={step.__typename}
              onChange={(value) => onChangeTextInput(value)}
              response={step.response}
            />

            <div className={styles["feedback-by-student-submit-button"]}>
              {step.__typename === "EndStep" && (
                <Button
                  variant="enclosed-primary"
                  onClick={() => handleContinueToNext()}
                  loading={submitLoading}
                  disabled={submitLoading}
                >
                  {t("feedback:submit")}
                </Button>
              )}
            </div>
          </div>
        </div>

        <SessionFeedbackModal
          isOpen={openModal}
          onClose={() => handleCloseFeedbackScreen()}
          onCancel={() => handleStayOnFeedbackScreen()}
        />
        <SessionFeedbackSubmissionErrorModal
          isOpen={isPostSessionFeedbackSubmissionError}
          loading={submitLoading}
          onCancelRetry={() => handleCloseFeedbackScreenAfterSubmissionError()}
          onReSubmit={() => handleRetrySubmission()}
        />
      </div>
    </div>
  );
};

export default FeedbackByStudentAfterJoinSession;
